import { z } from 'zod';
import {
  ADJUSTMENT_KINDS,
  ADJUSTMENT_MODES,
  PAYMENT_STATUSES,
} from '../../src/domain/finance/caseBill';
import {
  CalendarDateSchema,
  CaseReleaseSchema,
  IdSchema,
  InstantSchema,
  JobOrderNoSchema,
  MoneySchema,
  OmittedIdentitySchema,
} from './common.schema';
import { DeliverToSchema } from './geo.schema';

/** z.enum wants a non-empty tuple; the domain exports readonly arrays. */
function enumOf<T extends string>(values: readonly T[]) {
  return z.enum(values as unknown as [T, ...T[]]);
}

/** Where a case stands with its money: unpaid, part (some received and not all) or paid. */
export const PaymentStatusSchema = enumOf(PAYMENT_STATUSES);
export const AdjustmentKindSchema = enumOf(ADJUSTMENT_KINDS);
export const AdjustmentModeSchema = enumOf(ADJUSTMENT_MODES);

/**
 * A discount on one case, or a charge added to it. The request of 8 October 2026
 * (fourth). `value` is money (`amount`) or a percentage of the case's lines
 * (`percent`); `amount` is what it comes to, signed: below zero for a discount.
 * Who made it and when travel on the payment summary, not on the receipt.
 */
export const BillAdjustmentSchema = z.object({
  id: IdSchema,
  kind: AdjustmentKindSchema,
  mode: AdjustmentModeSchema,
  value: MoneySchema,
  amount: z.number(),
  reason: z.string(),
  byName: z.string().nullable().optional(),
  at: InstantSchema.optional(),
});

/**
 * A case's receipt, for Accounts: one line per item on each tray, with the teeth it
 * covers, the price of one and the value, and the total. Request of 5 October 2026.
 * The same rule as the case's value (the server's CaseReceipt and CasePricing), so the
 * receipt and the bill never disagree.
 */
export const ReceiptLineSchema = z.object({
  /** The line, for changing its price: `{kitId}|teeth|{itemId}|{product}` or `{kitId}|extra`. */
  key: z.string(),
  kitId: IdSchema,
  product: z.string(),
  category: z.string().nullable(),
  /** The teeth on the line; empty for units beyond the teeth marked. */
  teeth: z.array(z.string()),
  units: z.number().int().nonnegative(),
  unitPrice: MoneySchema,
  /** Charged once for the span, not per tooth. */
  perSpan: z.boolean(),
  value: MoneySchema,
  /** The price before Accounts changed it for this case, or null when it was not changed. */
  adjustedFrom: MoneySchema.nullable(),
});

/**
 * What an invoice is headed with: Mark paid shows a case as one. The lab's
 * request of 17 October 2026, item 12. The number on the doctor's paper, the
 * day the case was received and the day it is due, the doctor's ID, and
 * whether it was released to be shipped unpaid or has left. Whom it is to, by
 * name and with the clinic, only for a caller with seeIdentity: absent
 * otherwise, as on every payload.
 */
export const ReceiptInvoiceSchema = z.object({
  serialNumber: z.string().nullable(),
  receivedAt: InstantSchema,
  deliveryDate: CalendarDateSchema,
  doctorCode: z.string().nullable(),
  release: CaseReleaseSchema.nullable(),
  dispatched: z.boolean(),
  doctorName: OmittedIdentitySchema,
  patientName: OmittedIdentitySchema,
  deliverTo: DeliverToSchema.nullable().optional(),
});

export const ReceiptSchema = z.object({
  jobOrderNo: JobOrderNoSchema,
  /** False for a remake the lab does not charge for. */
  chargeable: z.boolean(),
  paid: z.boolean(),
  paidAt: InstantSchema.nullable(),
  lines: z.array(ReceiptLineSchema),
  /** What the lines come to. */
  total: MoneySchema,
  /*
   * The request of 8 October 2026 (fourth): the discounts and charges on the case,
   * what it owes after them, what has been paid of it and what is left. Absent from
   * an API older than they are.
   */
  adjustments: z.array(BillAdjustmentSchema).optional(),
  due: MoneySchema.optional(),
  paidAmount: MoneySchema.optional(),
  balance: MoneySchema.optional(),
  status: PaymentStatusSchema.optional(),
  /** The invoice's heading. Absent from an API older than it. */
  invoice: ReceiptInvoiceSchema.optional(),
});

/**
 * `PUT /job-orders/{jo}/receipt`: new prices for lines of this case only. The price
 * list is untouched. 409 on a paid case; 404 for a line not on the case.
 */
export const SaveReceiptSchema = z.object({
  lines: z
    .array(
      z.object({
        key: z.string().min(1),
        unitPrice: z.number().nonnegative('A price cannot be below zero.'),
      }),
    )
    .min(1),
});

export type Receipt = z.infer<typeof ReceiptSchema>;
export type BillAdjustmentRow = z.infer<typeof BillAdjustmentSchema>;
export type ReceiptLine = z.infer<typeof ReceiptLineSchema>;
export type ReceiptInvoice = z.infer<typeof ReceiptInvoiceSchema>;
